import React, { useCallback, useEffect, useMemo, useState } from "react";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { Download, BookOpen, Loader2, Sparkles, Search, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
};

const escapeReg = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
// Wrap matches in <mark> within text runs only (never inside tags) — the query is
// used purely as a regex pattern; the replacement re-wraps already-sanitised text.
const highlightHtml = (html, q) => {
  const term = (q || "").trim();
  if (term.length < 2) return html;
  const re = new RegExp(`(${escapeReg(term)})`, "gi");
  return html.replace(/>([^<]+)</g, (m, text) => `>${text.replace(re, '<mark class="manual-hl">$1</mark>')}<`);
};

export default function UserManualPanel({ jumpTo } = {}) {
  const [meta, setMeta] = useState(null);
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [query, setQuery] = useState("");
  const [matchCount, setMatchCount] = useState(0);

  const displayHtml = useMemo(() => highlightHtml(html, query), [html, query]);

  // After a search renders, count matches and scroll to the first one.
  useEffect(() => {
    const root = document.querySelector('[data-testid="user-manual-content"]');
    if (!root) return;
    const hits = root.querySelectorAll(".manual-hl");
    setMatchCount(hits.length);
    if (query.trim().length >= 2 && hits.length) {
      hits[0].scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [displayHtml, query]);

  // Deep-link a changelog note to its manual section (scroll + highlight) and/or
  // open the relevant admin panel — turning release notes into a guided tour.
  const goToSection = useCallback((sectionText) => {
    if (!sectionText) return;
    const root = document.querySelector('[data-testid="user-manual-content"]');
    if (!root) return;
    const needle = sectionText.toLowerCase();
    const heading = Array.from(root.querySelectorAll("h1,h2,h3,h4"))
      .find((h) => (h.textContent || "").toLowerCase().includes(needle));
    if (heading) {
      heading.scrollIntoView({ behavior: "smooth", block: "start" });
      heading.classList.add("manual-jump-flash");
      setTimeout(() => heading.classList.remove("manual-jump-flash"), 1600);
    }
  }, []);
  const openTab = useCallback((tab) => {
    if (tab) window.dispatchEvent(new CustomEvent("admin-switch-tab", { detail: tab }));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/user-manual");
      setMeta(data);
      setHtml(DOMPurify.sanitize(marked.parse(data.markdown || "")));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load the manual");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Contextual "?" from the header: once content is rendered, scroll+flash the
  // section for the panel the user came from.
  useEffect(() => {
    if (!jumpTo || loading || !html) return;
    const t = setTimeout(() => goToSection(jumpTo), 250);
    return () => clearTimeout(t);
  }, [jumpTo, loading, html, goToSection]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/admin/user-manual/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Ekselens-User-Manual-v${meta?.version || "1"}-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Manual PDF downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "PDF generation failed");
    } finally { setDownloading(false); }
  };

  return (
    <div data-testid="user-manual-panel" className="max-w-4xl">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div className="flex items-start gap-3">
          <div className="mt-1 h-10 w-10 rounded-lg bg-[#7737F6]/10 flex items-center justify-center flex-shrink-0">
            <BookOpen className="h-5 w-5 text-[#7737F6]" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-zinc-900">User &amp; Sales Manual</h2>
            <p className="text-sm text-zinc-500 mt-0.5">
              Plain-language guide to the two engines, the AI tools and every deliverable — read online or print to PDF.
            </p>
            {meta && (
              <p className="text-xs text-zinc-400 mt-2" data-testid="user-manual-version">
                Version <strong className="text-zinc-600">{meta.version}</strong> · Content updated {fmtDate(meta.updated)}
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={downloadPdf}
          disabled={downloading || loading}
          data-testid="user-manual-download-pdf"
          className="inline-flex items-center gap-2 rounded-lg bg-[#7737F6] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#6629d9] transition-colors disabled:opacity-60"
        >
          {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          {downloading ? "Generating…" : "Download as PDF"}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-zinc-500 text-sm py-12" data-testid="user-manual-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading manual…
        </div>
      ) : (
        <>
          <div className="relative mb-6" data-testid="user-manual-search-wrap">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="user-manual-search"
              placeholder="Search the manual — e.g. RAID, pricing, benefits…"
              className="w-full rounded-lg border border-zinc-200 bg-white pl-10 pr-24 py-2.5 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-[#7737F6]/30 focus:border-[#7737F6]"
            />
            {query.trim().length >= 2 && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2">
                <span className="text-xs text-zinc-400" data-testid="user-manual-search-count">
                  {matchCount} match{matchCount === 1 ? "" : "es"}
                </span>
                <button type="button" onClick={() => setQuery("")} data-testid="user-manual-search-clear"
                  className="text-zinc-400 hover:text-zinc-700"><X className="h-4 w-4" /></button>
              </div>
            )}
          </div>
          {Array.isArray(meta?.changelog) && meta.changelog.length > 0 && (
            <div className="mb-6 rounded-xl border border-[#7737F6]/25 bg-[#faf8ff] p-5" data-testid="user-manual-whats-new">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="h-4 w-4 text-[#7737F6]" />
                <h3 className="text-sm font-semibold uppercase tracking-[0.12em] text-[#7737F6]">What's new</h3>
              </div>
              <div className="space-y-3">
                {meta.changelog.map((entry) => (
                  <div key={entry.version} data-testid={`user-manual-changelog-${entry.version}`}>
                    <p className="text-xs text-zinc-500">
                      <strong className="text-zinc-800">Version {entry.version}</strong> · {fmtDate(entry.date)}
                    </p>
                    <ul className="mt-1 list-disc pl-5 text-sm text-zinc-700 space-y-1.5">
                      {(entry.notes || []).map((n, i) => {
                        const text = typeof n === "string" ? n : n.text;
                        const section = typeof n === "string" ? null : n.section;
                        const tab = typeof n === "string" ? null : n.tab;
                        return (
                          <li key={i}>
                            {text}
                            {(section || tab) && (
                              <span className="ml-1 inline-flex gap-2 align-baseline">
                                {section && (
                                  <button type="button" onClick={() => goToSection(section)}
                                    data-testid={`manual-note-goto-${entry.version}-${i}`}
                                    className="text-[11px] font-medium text-[#7737F6] hover:underline">
                                    Show me →
                                  </button>
                                )}
                                {tab && (
                                  <button type="button" onClick={() => openTab(tab)}
                                    data-testid={`manual-note-open-${entry.version}-${i}`}
                                    className="text-[11px] font-medium text-[#7737F6] hover:underline">
                                    Open {tab} ↗
                                  </button>
                                )}
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
          <article
            className="manual-prose rounded-xl border border-zinc-200 bg-white p-8"
            data-testid="user-manual-content"
            dangerouslySetInnerHTML={{ __html: displayHtml }}
          />
        </>
      )}
    </div>
  );
}
